import { funcionario as funcionario_api } from '@/service/api'
import { PersonInfo } from '@acme/core'
import CircularProgress from '@/global/components/CircularProgress'
import { FormFile } from 'global/components/Form'
import Dialog from 'global/guides/Dialog'
import { hasPermission } from 'hooks/useBackendMenu'
import { FrownIcon } from 'lucide-react'
import { useFuncionariosOutletContext } from 'pages/Funcionarios'
import { useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

export default function FacialModal() {
    const navigate = useNavigate()
    const { data: funcionarios } = useFuncionariosOutletContext()
    const { funcionarioId } = useParams()
    const funcionario = funcionarios?.find(
        item => item.id === Number(funcionarioId),
    )

    const { data, isLoading: isLoadingImage } =
        funcionario_api.reconhecimentoFacial.useFetchById(String(funcionarioId))

    const { mutateAsync, isLoading } =
        funcionario_api.reconhecimentoFacial.mutations.useCreate({
            onSuccess: () => {
                toast.success('Imagem facial cadastrada com sucesso.')
            },
            onError: (err: any) => {
                toast.error(err.message)
            },
        })

    const handleSubmit = useCallback(async ({ rosto }: { rosto: File }) => {
        const reader = new FileReader()

        reader.onload = async function () {
            const imageBase64 = reader.result
                ? reader.result.toString().split(',')[1]
                : ''

            return mutateAsync({
                imageBase64,
                funcionarioId: Number(funcionarioId),
            }).then(() => navigate(-1))
        }

        reader.readAsDataURL(rosto)
    }, [])

    if (!funcionario) return null

    return (
        <Dialog.Root
            open={hasPermission(['update', 'Funcionários'])}
            onSubmit={handleSubmit}
            height="auto"
            width="32rem"
            loading={isLoading}
        >
            <Dialog.Title>Cadastro facial</Dialog.Title>
            <Dialog.Content className="flex justify-center flex-col" dividers>
                <FormFile
                    name="rosto"
                    label={data ? 'Alterar a imagem' : 'Cadastrar imagem'}
                />
                <div className="flex flex-col items-center justify-center h-90 my-8">
                    <ImagePreview data={data} isLoadingImage={isLoadingImage} />
                </div>
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton />
            </Dialog.Actions>
        </Dialog.Root>
    )
}

interface ImagePreviewProps {
    data: PersonInfo | undefined
    isLoadingImage: boolean
}

function ImagePreview({ data, isLoadingImage }: ImagePreviewProps) {
    const { watch } = useFormContext()
    const [previewImage, setPreviewImage] = useState<string | null>(null)
    const rosto = watch('rosto')

    useEffect(() => {
        if (rosto && rosto instanceof File) {
            const reader = new FileReader()
            reader.onload = e => {
                setPreviewImage(e.target?.result as string)
            }
            reader.readAsDataURL(rosto)
        } else if (!rosto) {
            setPreviewImage(null)
        }
    }, [rosto])

    if (isLoadingImage) return <CircularProgress />
    if (previewImage)
        return (
            <img
                alt="Preview da imagem selecionada"
                src={previewImage}
                className="rounded-sm h-96"
            />
        )
    if (data && data?.faces?.at(0)?.image?.url)
        return (
            <img
                alt={data.personInformation.name}
                src={data.faces[0].image.url}
                className="rounded-sm h-96"
            />
        )
    return (
        <div className="flex flex-col items-center gap-4">
            <FrownIcon className="size-20" />
            <h2 className="uppercase select-none">Sem cadastro facial</h2>
        </div>
    )
}
